import React, { useEffect, useState } from 'react';
import { formatDate, formatDuration, formatDbDate } from '../utils/dateUtils';

const Lightbox = ({
  type,
  onClose,
  apiResponse,
  dbLog,
  activeTickets,
  ticketEvents,
  environment,
  setEnvironment,
  onFetchData,
  isLoading,
  partDetails,
  partDetailsError,
  onOpenLightbox
}) => {
  const [operationsExpanded, setOperationsExpanded] = useState(false);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  if (!type) return null;

  const getTitle = () => {
    switch (type) {
      case 'fetch-data':
        return 'Environment Settings';
      case 'api-response':
        return 'API Response';
      case 'db-log':
        return 'Database Operations Log';
      default:
        return 'Lightbox';
    }
  };

  const getUniqueWorkCenterStatuses = (entries) => {
    const statuses = [];
    if (Array.isArray(entries)) {
      entries.forEach(entry => {
        if (entry.workcenterStatus && !statuses.includes(entry.workcenterStatus)) {
          statuses.push(entry.workcenterStatus);
        }
      });
    }
    return statuses.sort();
  };

  const renderFetchDataContent = () => (
    <div className="form-container">
      <form onSubmit={onFetchData}>
        <div className="environment-toggle-container">
          <label className="environment-toggle-label">Environment</label>
          <div className="environment-toggle">
            <span className={`toggle-option ${environment === 'production' ? 'active' : ''}`}>
              Production
            </span>
            <button
              type="button"
              className={`toggle-switch ${environment === 'test' ? 'test' : 'production'}`}
              onClick={() => setEnvironment(environment === 'production' ? 'test' : 'production')}
              aria-label="Toggle environment"
            >
              <span className="toggle-slider"></span>
            </button>
            <span className={`toggle-option ${environment === 'test' ? 'active' : ''}`}>
              Test
            </span>
          </div>
        </div>
        <div className="form-group">
          <button type="submit" disabled={isLoading} className="fetch-data-button">
            {isLoading ? (
              <span className="loading-spinner"></span>
            ) : (
              <i className="fas fa-sync"></i>
            )}
            Fetch Data
          </button>
        </div>
      </form>
    </div>
  );

  const renderApiResponseContent = () => {
    if (apiResponse?.error) {
      return <div className="error">{apiResponse.error}</div>;
    }

    if (!apiResponse?.response) {
      return <p>No API response data available.</p>;
    }

    const uniqueStatuses = getUniqueWorkCenterStatuses(apiResponse.response);

    return (
      <>
                 <div className="raw-data">
           <h4>Raw Response Data:</h4>
           <textarea
             readOnly
             value={JSON.stringify(apiResponse, null, 2)}
           />
         </div>

         {/* MDM Parts API Response */}
         <div className="raw-data">
           <h4>MDM Parts API Response:</h4>
           <div style={{
             fontSize: '12px',
             backgroundColor: '#f8f9fa',
             border: '1px solid #dee2e6',
             borderRadius: '4px',
             padding: '10px',
             marginBottom: '10px',
             fontFamily: 'monospace'
           }}>
             <strong>Endpoint:</strong> /mdm/v1/parts<br/>
             <strong>Description:</strong> Master Data Management Parts API<br/>
             <strong>URL:</strong> https://{environment === 'test' ? 'test.' : ''}connect.plex.com/mdm/v1/parts/{(() => {
               if (activeTickets && activeTickets.length > 0 && apiResponse?.response) {
                 const mostRecentTicket = activeTickets[0];
                 const matchingRecord = apiResponse.response.find(record =>
                   record.jobNumber === mostRecentTicket.job_number
                 );
                 return matchingRecord?.partId || 'NOT_FOUND';
               }
               return 'NO_TICKET';
             })()}
           </div>

           {partDetailsError ? (
             <div className="error">
               <strong>Error:</strong> {partDetailsError}
             </div>
           ) : partDetails && partDetails.results ? (
             (() => {
               const mdmResult = partDetails.results.find(r => r.endpoint === 'MDM Parts');
               if (!mdmResult) {
                 return <p>No MDM Parts API result available.</p>;
               }
               return (
                 <div>
                   <div style={{
                     marginBottom: '10px',
                     padding: '8px',
                     backgroundColor: mdmResult.success ? '#e8f5e8' : '#f8d7da',
                     borderRadius: '4px',
                     border: `2px solid ${mdmResult.success ? '#28a745' : '#dc3545'}`
                   }}>
                     <strong>Status:</strong> {mdmResult.success ? 'SUCCESS' : 'FAILED'} ({mdmResult.status})
                   </div>
                   {mdmResult.success ? (
                     <textarea
                       readOnly
                       value={JSON.stringify(mdmResult.data, null, 2)}
                       style={{ width: '100%', height: '300px', fontSize: '11px' }}
                     />
                   ) : (
                     <div style={{ padding: '8px', backgroundColor: '#f8d7da', borderRadius: '4px', fontSize: '12px' }}>
                       <strong>Error:</strong> {mdmResult.error}
                       {mdmResult.details && (
                         <div style={{ marginTop: '5px' }}>
                           <strong>Details:</strong> {JSON.stringify(mdmResult.details, null, 2)}
                         </div>
                       )}
                     </div>
                   )}
                 </div>
               );
             })()
           ) : (
             <p>No MDM Parts API data available. Data will appear here when a ticket is displayed.</p>
           )}
         </div>

        {uniqueStatuses.length > 0 && (
          <div className="status-list">
            <h4>Work Center Statuses:</h4>
            <ul>
              {uniqueStatuses.map((status, index) => (
                <li key={index}>{status}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Debug Info */}
        <div className="debug-info">
          <h4>Debug Info</h4>
          <p><strong>Total tickets for timer display:</strong> {activeTickets?.length || 0}</p>
          {(!activeTickets || activeTickets.length === 0) ? (
            <p style={{ color: 'red' }}>No tickets found for timer display!</p>
          ) : (
            <p style={{ color: 'green' }}>Found {activeTickets.length} tickets for timer display</p>
          )}
          {activeTickets && activeTickets.map((ticket, index) => (
            <p key={index}>
              Ticket ID: {ticket.ticket_id}, Workcenter: {ticket.workcenter_code},
              Job: {ticket.job_number}, Active: {ticket.is_active ? 'Yes' : 'No'},
              Created: {formatDate(ticket.timestamp)}
            </p>
          ))}
        </div>

        <h4>Formatted Data:</h4>
        <table className="data-table">
          <thead>
            <tr>
              <th>Work Center</th>
              <th>Status</th>
              <th>Log Date</th>
              <th>Job Number</th>
              <th>Part Number</th>
              <th>Revision</th>
              <th>Operation</th>
              <th>Duration</th>
            </tr>
          </thead>
          <tbody>
            {apiResponse.response.map((entry, index) => (
              <tr key={index}>
                <td>{entry.workcenterCode || ''}</td>
                <td>{entry.workcenterStatus || ''}</td>
                <td>{formatDate(entry.logDate || '')}</td>
                <td>{entry.jobNumber || ''}</td>
                <td>{entry.partNumber || ''}</td>
                <td>{entry.revision || ''}</td>
                <td>{entry.operationCode || ''}</td>
                <td>{formatDuration(entry.duration || '')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  };

  const renderDbLogContent = () => {
    // Sort events by event_date in descending order (most recent first)
    const sortedEvents = ticketEvents ? [...ticketEvents].sort((a, b) =>
      new Date(b.event_date) - new Date(a.event_date)
    ) : [];

    return (
      <>
        {/* Ticket Events (Database Records) */}
        <div className="ticket-events-section">
          <h4>Ticket Events (Database Records)</h4>
          {sortedEvents.length === 0 ? (
            <p>No ticket events found.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Ticket ID</th>
                  <th>Workcenter</th>
                  <th>Job Number</th>
                  <th>Event Sequence</th>
                  <th>Status</th>
                  <th>Event Date</th>
                  <th>Operation Code</th>
                  <th>Active</th>
                </tr>
              </thead>
              <tbody>
                {sortedEvents.map((event, index) => {
                  // Build CSS classes for styling
                  const rowClasses = [];

                  // Status-specific classes
                  const status = event.status.toLowerCase();
                  if (['alerted', 'acknowledged', 'evaluated'].includes(status)) {
                    rowClasses.push(`status-${status}`);
                  } else {
                    rowClasses.push('status-resolved');
                  }

                  const rowClass = rowClasses.join(' ');

                  return (
                    <tr key={index} className={rowClass}>
                      <td>{event.ticket_id}</td>
                      <td>{event.workcenter_code}</td>
                      <td>{event.job_number}</td>
                      <td>{event.event_sequence}</td>
                      <td>{event.status}</td>
                      <td>{formatDbDate(event.event_date)}</td>
                      <td>{event.operation_code || ''}</td>
                      <td>{event.is_active ? 'Yes' : 'No'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Database Operations Summary */}
        <div className="db-log-summary">
          <h4>Summary</h4>
          <p><strong>Total Operations:</strong> {dbLog.length}</p>
          <p><strong>Total Ticket Events:</strong> {sortedEvents.length}</p>
        </div>

        {/* Database Operations Details (Accordion) */}
        <div className="db-operations">
          <div className={`debug-accordion ${operationsExpanded ? 'expanded' : ''}`}>
            <div className="debug-accordion-header" onClick={() => setOperationsExpanded(!operationsExpanded)}>
              <span className="debug-accordion-icon">
                {operationsExpanded ? '▲' : '▼'}
              </span>
              <span className="debug-accordion-title">Operation Details</span>
            </div>
            <div className="debug-accordion-content">
              {dbLog.length === 0 ? (
                <p>No database operations logged.</p>
              ) : (
                <table className="db-log-table">
                  <thead>
                    <tr>
                      <th>Timestamp</th>
                      <th>Operation</th>
                      <th>Data</th>
                      <th>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dbLog.map((log, index) => (
                      <tr key={index}>
                        <td>{log.timestamp}</td>
                        <td>{log.operation}</td>
                        <td>
                          {log.data ? (
                            <pre>{JSON.stringify(log.data, null, 2)}</pre>
                          ) : (
                            <em>No data</em>
                          )}
                        </td>
                        <td>
                          {log.result !== null ? (
                            typeof log.result === 'boolean' ? (
                              log.result ? '✅ Success' : '❌ Failed'
                            ) : (
                              <pre>{JSON.stringify(log.result, null, 2)}</pre>
                            )
                          ) : (
                            <em>No result</em>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderContent = () => {
    switch (type) {
      case 'fetch-data':
        return renderFetchDataContent();
      case 'api-response':
        return renderApiResponseContent();
      case 'db-log':
        return renderDbLogContent();
      default:
        return <p>Unknown lightbox type.</p>;
    }
  };

  return (
    <div className="lightbox-overlay" onClick={onClose}>
      <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
        <div className="lightbox-header">
          <h3>{getTitle()}</h3>
          <button className="lightbox-close" onClick={onClose}>×</button>
        </div>
        <div className="lightbox-body">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Lightbox;